import { createCollection, type Collection } from '@tanstack/react-db';
import { queryCollectionOptions } from '@tanstack/query-db-collection';
import type { QueryClient } from '@tanstack/react-query';
import { fetchUserMe } from './api';
import type { UserWithTenantRole } from './models';

/**
 * Creates a collection for the current user with TanStack Query integration.
 *
 * Note: Type assertion is needed due to a mismatch between queryCollectionOptions
 * returning key type `string` and createCollection expecting `string | number`.
 * This is a known limitation in TanStack DB beta.
 * See: https://github.com/TanStack/db/issues/675
 */
export const createUserMeCollection = (
  tenantId: string,
  queryClient: QueryClient
): Collection<UserWithTenantRole> =>
  createCollection<UserWithTenantRole>(
    queryCollectionOptions({
      queryKey: ['me', tenantId],
      queryFn: async () => {
        const user = await fetchUserMe(tenantId);
        // Return as array for collection, will be accessed as single result
        return user ? [user] : [];
      },
      queryClient,
      getKey: (item) => item.id,
      staleTime: Infinity, // Master data - only invalidated by mutations
    }) as never
  );

export type UserMeCollection = Collection<UserWithTenantRole>;
